/**
 * RR Integral — Theme JavaScript
 * Ferretería Técnica · Rancagua, Chile
 */
'use strict';

const RR = (() => {

  /* ============================================================
     UTILITIES
     ============================================================ */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const fmt = (cents) => {
    const n = cents / 100;
    return '$' + n.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  /* ============================================================
     TOAST
     ============================================================ */
  const toast = (() => {
    const el = document.getElementById('rr-toast');
    const msg = document.getElementById('rr-toast-msg');
    let timer = null;

    function show(text = 'Producto agregado', type = 'success') {
      if (!el) return;
      if (timer) clearTimeout(timer);
      msg.textContent = text;
      el.classList.remove('is-visible');
      // Force reflow
      void el.offsetWidth;
      el.classList.add('is-visible');
      timer = setTimeout(() => el.classList.remove('is-visible'), 3100);
    }

    return { show };
  })();

  /* ============================================================
     OVERLAY
     ============================================================ */
  const overlay = {
    el: document.getElementById('rr-overlay'),
    show() { this.el && this.el.classList.add('is-visible'); },
    hide() { this.el && this.el.classList.remove('is-visible'); }
  };

  /* ============================================================
     CLOSE ALL DRAWERS
     ============================================================ */
  function closeAll() {
    cartDrawer.close();
    mobileNav.close();
    overlay.hide();
  }

  /* ============================================================
     MOBILE NAV
     ============================================================ */
  const mobileNav = (() => {
    const nav = document.getElementById('mobile-nav');
    const hamburger = document.getElementById('header-hamburger');

    function open() {
      nav && nav.classList.add('is-open');
      hamburger && hamburger.classList.add('is-open');
      overlay.show();
      document.body.style.overflow = 'hidden';
    }

    function close() {
      nav && nav.classList.remove('is-open');
      hamburger && hamburger.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    function toggle() {
      nav && nav.classList.contains('is-open') ? close() : open();
    }

    hamburger && hamburger.addEventListener('click', toggle);

    const closeBtn = document.getElementById('mobile-nav-close');
    closeBtn && closeBtn.addEventListener('click', () => { close(); overlay.hide(); });

    return { open, close, toggle };
  })();

  /* ============================================================
     STICKY HEADER
     ============================================================ */
  (() => {
    const header = document.getElementById('site-header');
    if (!header) return;
    let lastY = 0;
    const HIDE_THRESHOLD = 150;

    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      if (y > HIDE_THRESHOLD && y > lastY) {
        header.style.transform = 'translateY(-100%)';
      } else {
        header.style.transform = 'translateY(0)';
      }
      lastY = y;
    }, { passive: true });
  })();

  /* ============================================================
     CART STATE
     ============================================================ */
  const cart = {
    data: null,

    async fetch() {
      try {
        const r = await fetch('/cart.js');
        this.data = await r.json();
        return this.data;
      } catch (e) {
        console.error('Cart fetch error', e);
        return null;
      }
    },

    async add(variantId, qty = 1) {
      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: qty })
        });
        if (!r.ok) {
          const err = await r.json();
          throw new Error(err.description || 'No se pudo agregar al carrito');
        }
        return await r.json();
      } catch (e) {
        console.error('Cart add error', e);
        throw e;
      }
    },

    async change(line, qty) {
      try {
        const r = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity: qty })
        });
        this.data = await r.json();
        return this.data;
      } catch (e) {
        console.error('Cart change error', e);
        throw e;
      }
    },

    async remove(line) {
      return this.change(line, 0);
    },

    async clear() {
      try {
        const r = await fetch('/cart/clear.js', { method: 'POST' });
        this.data = await r.json();
        return this.data;
      } catch (e) {
        console.error('Cart clear error', e);
        throw e;
      }
    }
  };

  /* ============================================================
     CART BADGE
     ============================================================ */
  function updateCartBadge(count) {
    $$('[data-cart-count]').forEach(el => {
      el.setAttribute('data-count', count);
      el.textContent = count;
      el.classList.add('bump');
      setTimeout(() => el.classList.remove('bump'), 350);
    });
  }

  /* ============================================================
     CART DRAWER
     ============================================================ */
  const cartDrawer = (() => {
    const drawer = document.getElementById('cart-drawer');
    const body = document.getElementById('cart-drawer-body');
    const foot = document.getElementById('cart-drawer-foot');
    const titleEl = document.getElementById('cart-drawer-title');

    function open() {
      if (!drawer) return;
      drawer.classList.add('is-open');
      overlay.show();
      document.body.style.overflow = 'hidden';
      refresh();
    }

    function close() {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      overlay.hide();
      document.body.style.overflow = '';
    }

    function toggle() {
      drawer && drawer.classList.contains('is-open') ? close() : open();
    }

    async function refresh() {
      const data = await cart.fetch();
      if (!data) return;
      updateCartBadge(data.item_count);
      render(data);
    }

    function buildWALink(data) {
      const waNumber = '56982993711';
      let lines = data.items.map(item =>
        `• ${item.title} x${item.quantity} — ${fmt(item.line_price)}`
      ).join('\n');
      const total = fmt(data.total_price);
      const text = `Hola! Quiero cotizar los siguientes productos de RR Integral:\n\n${lines}\n\nTotal: ${total}`;
      return `https://wa.me/${waNumber}?text=${encodeURIComponent(text)}`;
    }

    function render(data) {
      if (!body) return;

      if (titleEl) {
        titleEl.textContent = `Tu carrito (${data.item_count})`;
      }

      if (data.item_count === 0) {
        body.innerHTML = `
          <div class="cart-empty">
            <div class="cart-empty__icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <path d="M16 10a4 4 0 01-8 0"/>
              </svg>
            </div>
            <div class="cart-empty__title">Tu carrito está vacío</div>
            <div class="cart-empty__sub">Agrega productos para comenzar tu compra.</div>
            <a href="/collections/all" class="btn btn--primary" onclick="RR.closeAll()">
              Explorar productos
            </a>
          </div>`;
        if (foot) foot.style.display = 'none';
        return;
      }

      if (foot) foot.style.display = '';

      body.innerHTML = data.items.map((item, idx) => `
        <div class="cart-item" data-line="${idx + 1}">
          <img class="cart-item__img"
            src="${item.image ? item.image.replace('.jpg', '_80x80.jpg').replace('.png', '_80x80.png').replace('.webp', '_80x80.webp') : ''}"
            alt="${item.title}"
            onerror="this.src='//:0'; this.style.background='var(--c-bg1)'">
          <div class="cart-item__info">
            <div class="cart-item__brand">${item.vendor || ''}</div>
            <div class="cart-item__name">${item.product_title}</div>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<div style="font-size:11.5px;color:var(--c-steel);margin-bottom:6px;">${item.variant_title}</div>` : ''}
            <div class="cart-item__controls">
              <div class="cart-item__qty">
                <div class="cart-item__qty-btn" data-action="decrease" data-line="${idx + 1}" data-qty="${item.quantity}">−</div>
                <div class="cart-item__qty-num">${item.quantity}</div>
                <div class="cart-item__qty-btn" data-action="increase" data-line="${idx + 1}" data-qty="${item.quantity}">+</div>
              </div>
              <div class="cart-item__price">${fmt(item.line_price)}</div>
            </div>
            <div class="cart-item__remove" data-line="${idx + 1}">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"/></svg>
              Eliminar
            </div>
          </div>
        </div>`).join('');

      // Subtotal & footer
      if (foot) {
        const subtotalEl = foot.querySelector('[data-cart-subtotal]');
        const checkoutBtn = foot.querySelector('[data-checkout-btn]');
        const waBtn = foot.querySelector('[data-cart-wa]');
        if (subtotalEl) subtotalEl.textContent = fmt(data.total_price);
        if (waBtn) waBtn.href = buildWALink(data);
        if (checkoutBtn) {
          checkoutBtn.textContent = `Ir a pagar · ${fmt(data.total_price)}`;
        }
      }

      // Events on cart items
      body.querySelectorAll('.cart-item__qty-btn').forEach(btn => {
        btn.addEventListener('click', async () => {
          const line = parseInt(btn.dataset.line);
          const action = btn.dataset.action;
          const current = parseInt(btn.dataset.qty);
          const newQty = action === 'increase' ? current + 1 : Math.max(0, current - 1);
          await cart.change(line, newQty);
          await refresh();
        });
      });

      body.querySelectorAll('.cart-item__remove').forEach(btn => {
        btn.addEventListener('click', async () => {
          const line = parseInt(btn.dataset.line);
          await cart.remove(line);
          await refresh();
        });
      });
    }

    // Init cart count on page load
    (async () => {
      const data = await cart.fetch();
      if (data) updateCartBadge(data.item_count);
    })();

    // Open cart button
    $$('[data-open-cart]').forEach(el => {
      el.addEventListener('click', toggle);
    });

    // Close button
    $$('[data-close-cart]').forEach(el => {
      el.addEventListener('click', close);
    });

    return { open, close, toggle, refresh };
  })();

  /* ============================================================
     ADD TO CART
     ============================================================ */
  async function addToCart(variantId, qty = 1, btn = null) {
    if (btn) {
      btn.classList.add('btn--loading');
      btn.disabled = true;
    }
    try {
      await cart.add(variantId, qty);
      await cartDrawer.refresh();
      cartDrawer.open();
      toast.show('Producto agregado al carrito');
    } catch (e) {
      toast.show(e.message || 'Error al agregar al carrito', 'error');
    } finally {
      if (btn) {
        btn.classList.remove('btn--loading');
        btn.disabled = false;
      }
    }
  }

  // Delegate: product card add buttons
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-add-to-cart]');
    if (!btn) return;
    e.preventDefault();
    const variantId = btn.dataset.variantId || btn.dataset.addToCart;
    if (!variantId || btn.disabled) return;
    addToCart(variantId, 1, btn);
  });

  /* ============================================================
     PRODUCT PAGE — QTY SELECTOR & ADD BTN
     ============================================================ */
  (() => {
    const qtyNum = document.getElementById('product-qty-num');
    const qtyDec = document.getElementById('product-qty-dec');
    const qtyInc = document.getElementById('product-qty-inc');
    const addBtn = document.getElementById('product-add-btn');
    const variantInput = document.getElementById('product-variant-id');

    if (!addBtn || !variantInput) return;

    let qty = 1;

    function updateQty() {
      if (qtyNum) qtyNum.textContent = qty;
    }

    qtyDec && qtyDec.addEventListener('click', () => {
      qty = Math.max(1, qty - 1);
      updateQty();
    });

    qtyInc && qtyInc.addEventListener('click', () => {
      qty = Math.min(99, qty + 1);
      updateQty();
    });

    addBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const id = variantInput.value;
      if (!id) return;
      addToCart(id, qty, addBtn);
    });

    // Gallery thumbnails
    $$('.product-gallery__thumb').forEach((thumb, idx) => {
      thumb.addEventListener('click', () => {
        $$('.product-gallery__thumb').forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
        const mainImg = document.getElementById('product-main-img');
        const src = thumb.dataset.src;
        if (mainImg && src) {
          mainImg.src = src;
        }
      });
    });

    // Product detail tabs
    $$('.product-details__tab').forEach(tab => {
      tab.addEventListener('click', () => {
        const target = tab.dataset.tab;
        $$('.product-details__tab').forEach(t => t.classList.remove('is-active'));
        $$('.product-details__content').forEach(c => c.classList.remove('is-active'));
        tab.classList.add('is-active');
        const content = document.getElementById(`tab-${target}`);
        if (content) content.classList.add('is-active');
      });
    });
  })();

  /* ============================================================
     SEARCH
     ============================================================ */
  $$('[data-search-form]').forEach(form => {
    form.addEventListener('submit', (e) => {
      const input = form.querySelector('input[name="q"]');
      if (input && !input.value.trim()) {
        e.preventDefault();
      }
    });
  });

  /* ============================================================
     WHATSAPP BUILDER
     ============================================================ */
  function buildWAUrl(text) {
    return `https://wa.me/56982993711?text=${encodeURIComponent(text)}`;
  }

  /* ============================================================
     SMOOTH SCROLL TO TOP
     ============================================================ */
  $$('[data-scroll-top]').forEach(el => {
    el.addEventListener('click', (e) => {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  });

  /* ============================================================
     ACTIVE NAV LINK
     ============================================================ */
  (() => {
    const path = window.location.pathname;
    $$('.header-nav__link').forEach(link => {
      if (link.getAttribute('href') === path) {
        link.classList.add('is-active');
      }
    });
  })();

  /* ============================================================
     COLLECTION SORT
     ============================================================ */
  const sortSelect = document.getElementById('sort-by');
  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      window.location.href = url.toString();
    });
  }

  /* ============================================================
     ANNOUNCEMENT BAR CLOSE
     ============================================================ */
  const announcementClose = document.getElementById('announcement-close');
  const announcementBar = document.getElementById('announcement-bar');
  if (announcementClose && announcementBar) {
    announcementClose.addEventListener('click', () => {
      announcementBar.style.height = announcementBar.offsetHeight + 'px';
      requestAnimationFrame(() => {
        announcementBar.style.transition = 'height .3s, opacity .3s';
        announcementBar.style.height = '0';
        announcementBar.style.overflow = 'hidden';
        announcementBar.style.opacity = '0';
      });
      setTimeout(() => announcementBar.remove(), 350);
    });
  }

  /* ============================================================
     ESCAPE KEY HANDLER
     ============================================================ */
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAll();
  });

  return {
    closeAll,
    toast,
    cart,
    cartDrawer,
    addToCart,
    buildWAUrl
  };
})();
